// User preferences management with localStorage persistence

export interface UserPreferences {
  isDarkMode: boolean | null; // null means follow system preference
  isLocationTrackingEnabled: boolean;
  selectedView: "map" | "list";
  selectedFilters: string[];
  lastKnownLocation: [number, number] | null;
  defaultMapType: string;
}

const STORAGE_KEY = "loppehub_preferences";

const defaultPreferences: UserPreferences = {
  isDarkMode: null, // Follow system preference by default
  isLocationTrackingEnabled: true,
  selectedView: "map",
  selectedFilters: [],
  lastKnownLocation: null,
  defaultMapType: "roadmap",
};

export class UserPreferencesManager {
  private preferences: UserPreferences;
  private listeners: Array<(prefs: UserPreferences) => void> = [];

  constructor() {
    this.preferences = this.loadPreferences();
  }

  private loadPreferences(): UserPreferences {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new preference keys
        return { ...defaultPreferences, ...parsed };
      }
    } catch (error) {
      console.warn("Failed to load user preferences:", error);
    }
    return { ...defaultPreferences };
  }

  private savePreferences() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.preferences));
      console.log("💾 [Preferences] Saved:", this.preferences);
    } catch (error) {
      console.warn("Failed to save user preferences:", error);
    }
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener(this.preferences));
  }

  // Getters
  get isDarkMode(): boolean | null {
    return this.preferences.isDarkMode;
  }

  get isLocationTrackingEnabled(): boolean {
    return this.preferences.isLocationTrackingEnabled;
  }

  get selectedView(): "map" | "list" {
    return this.preferences.selectedView;
  }

  get selectedFilters(): string[] {
    return this.preferences.selectedFilters;
  }

  get lastKnownLocation(): [number, number] | null {
    return this.preferences.lastKnownLocation;
  }

  get defaultMapType(): string {
    return this.preferences.defaultMapType;
  }

  get all(): UserPreferences {
    return { ...this.preferences };
  }

  // Setters
  setDarkMode(isDarkMode: boolean | null) {
    if (this.preferences.isDarkMode !== isDarkMode) {
      this.preferences.isDarkMode = isDarkMode;
      this.savePreferences();
      this.notifyListeners();
    }
  }

  setLocationTrackingEnabled(enabled: boolean) {
    if (this.preferences.isLocationTrackingEnabled !== enabled) {
      this.preferences.isLocationTrackingEnabled = enabled;
      this.savePreferences();
      this.notifyListeners();
    }
  }

  setSelectedView(view: "map" | "list") {
    if (this.preferences.selectedView !== view) {
      this.preferences.selectedView = view;
      this.savePreferences();
      this.notifyListeners();
    }
  }

  setSelectedFilters(filters: string[]) {
    if (JSON.stringify(this.preferences.selectedFilters) !== JSON.stringify(filters)) {
      this.preferences.selectedFilters = [...filters];
      this.savePreferences();
      this.notifyListeners();
    }
  }

  setLastKnownLocation(location: [number, number] | null) {
    if (JSON.stringify(this.preferences.lastKnownLocation) !== JSON.stringify(location)) {
      this.preferences.lastKnownLocation = location;
      this.savePreferences();
      this.notifyListeners();
    }
  }

  setDefaultMapType(mapType: string) {
    if (this.preferences.defaultMapType !== mapType) {
      this.preferences.defaultMapType = mapType;
      this.savePreferences();
      this.notifyListeners();
    }
  }

  // Bulk update
  updatePreferences(updates: Partial<UserPreferences>) {
    let changed = false;

    Object.keys(updates).forEach((key) => {
      const typedKey = key as keyof UserPreferences;
      if (JSON.stringify(this.preferences[typedKey]) !== JSON.stringify(updates[typedKey])) {
        (this.preferences as any)[typedKey] = updates[typedKey];
        changed = true;
      }
    });

    if (changed) {
      this.savePreferences();
      this.notifyListeners();
    }
  }

  // Event listener for preference changes
  addListener(listener: (prefs: UserPreferences) => void) {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Clear all preferences (reset to defaults)
  reset() {
    this.preferences = { ...defaultPreferences };
    this.savePreferences();
    this.notifyListeners();
  }

  // Export preferences for debugging
  export(): string {
    return JSON.stringify(this.preferences, null, 2);
  }

  // Import preferences (for debugging/migration)
  import(preferencesJson: string): boolean {
    try {
      const imported = JSON.parse(preferencesJson);
      this.preferences = { ...defaultPreferences, ...imported };
      this.savePreferences();
      this.notifyListeners();
      return true;
    } catch (error) {
      console.error("Failed to import preferences:", error);
      return false;
    }
  }
}

// Singleton instance
export const userPreferences = new UserPreferencesManager();

// Make it available for debugging - declaration is in debug.ts
